import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { getSearchQuota } from '../services/api';
import { SearchQuota } from '../types';

interface SearchDrawerContextType {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  drawerClosing: boolean;
  drawerClosed: () => void;
  searchCreatedKey: number;
  createdSearchIds: string[] | null;
  consumeSearchCreated: () => void;
  notifySearchCreated: (ids: string[]) => void;
  /** Current search-slot quota, or null until loaded / when unauthenticated. */
  quota: SearchQuota | null;
  /** True when the user has no slots left — gate "New search" entry points. */
  atSearchLimit: boolean;
  /** Re-fetch the quota (e.g. after a search is deleted). */
  refreshQuota: () => void;
}

const SearchDrawerContext = createContext<SearchDrawerContextType>({
  isOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
  drawerClosing: false,
  drawerClosed: () => {},
  searchCreatedKey: 0,
  createdSearchIds: null,
  consumeSearchCreated: () => {},
  notifySearchCreated: () => {},
  quota: null,
  atSearchLimit: false,
  refreshQuota: () => {},
});

export const useSearchDrawer = () => useContext(SearchDrawerContext);

interface SearchDrawerProviderProps {
  children: ReactNode;
}

export const SearchDrawerProvider: React.FC<SearchDrawerProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchCreatedKey, setSearchCreatedKey] = useState(0);
  const [createdSearchIds, setCreatedSearchIds] = useState<string[] | null>(null);
  const [drawerClosing, setDrawerClosing] = useState(false);
  const [quota, setQuota] = useState<SearchQuota | null>(null);

  const openDrawer = () => setIsOpen(true);
  const closeDrawer = () => {
    setDrawerClosing(true);
    setIsOpen(false);
  };
  const drawerClosed = useCallback(() => setDrawerClosing(false), []);
  const consumeSearchCreated = useCallback(() => setCreatedSearchIds(null), []);
  const notifySearchCreated = (ids: string[]) => {
    setCreatedSearchIds(ids);
    setSearchCreatedKey((prev) => prev + 1);
  };

  const refreshQuota = useCallback(async () => {
    // The provider wraps the whole app (incl. public pages); skip the call when
    // there's no session so we don't trip the 401 interceptor on /signin etc.
    if (!localStorage.getItem('accessToken')) return;
    try {
      setQuota(await getSearchQuota());
    } catch {
      // Non-blocking: on failure leave the gate open (backend still enforces).
    }
  }, []);

  // Load on mount (for already-authenticated users) and after each create.
  useEffect(() => {
    refreshQuota();
  }, [refreshQuota, searchCreatedKey]);

  const atSearchLimit = quota ? quota.remaining <= 0 : false;

  return (
    <SearchDrawerContext.Provider
      value={{
        isOpen,
        openDrawer,
        closeDrawer,
        drawerClosing,
        drawerClosed,
        searchCreatedKey,
        createdSearchIds,
        consumeSearchCreated,
        notifySearchCreated,
        quota,
        atSearchLimit,
        refreshQuota,
      }}
    >
      {children}
    </SearchDrawerContext.Provider>
  );
};
